import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { keys, type Sample } from '@/api';
import { SPACE } from '@/lib/space';

export interface SampleMessage {
	instanceId: string;
	slug: string;
	sample: Sample;
}

/**
 * Subscribe to the admin SSE stream (≤ 5 Hz per instance). `onSample` is called outside React
 * rendering; callers buffer into refs and re-render on their own cadence. Fleet changes refresh
 * instance queries.
 */
export function useSampleStream(ids: string[] | null, onSample: (m: SampleMessage) => void) {
	const qc = useQueryClient();
	const cb = useRef(onSample);
	cb.current = onSample;
	const idKey = ids === null ? '*' : ids.join(',');
	useEffect(() => {
		if (idKey === '') return;
		// EventSource cannot send the space header: it goes in the query.
		const url = idKey === '*' ? `/api/v1/stream?space=${SPACE}` : `/api/v1/stream?space=${SPACE}&ids=${encodeURIComponent(idKey)}`;
		const es = new EventSource(url);
		es.addEventListener('sample', (e) => cb.current(JSON.parse((e as MessageEvent).data) as SampleMessage));
		es.addEventListener('fleet', () => {
			qc.invalidateQueries({ queryKey: keys.instances });
			qc.invalidateQueries({ queryKey: keys.status });
		});
		return () => es.close();
	}, [idKey, qc]);
}
